import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Badge, Menu } from 'antd';
import {
  DashboardOutlined,
  TableOutlined,
  TeamOutlined,
  MessageOutlined,
  ShoppingCartOutlined,
  WifiOutlined,
  StarOutlined,
  SettingOutlined,
  HomeOutlined,
  CloudServerOutlined,
  PhoneOutlined,
  MailOutlined,
  CustomerServiceOutlined,
  ArrowLeftOutlined,
  RocketOutlined,
  GlobalOutlined,
  InboxOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ApartmentOutlined,
  CheckSquareOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import type { MenuProps } from 'antd';
import { useRootStore } from '../../app/providers/StoreProvider';
import { globalChatStore } from '../../entities/crm/chat-store';
import { sidebarCountersStore } from '../../entities/sidebar-counters/sidebar-counters-store';
import { changelogStore } from '../../entities/changelog/changelog-store';
import { useOpsTodayFlag } from '../../features/ops-today/useOpsTodayFlag';
import { brand } from '@/shared/lib/brand';
import { shellChrome } from '../../shared/stores/shell-chrome';

interface SidebarProps {
  collapsed: boolean;
  onNavigate?: () => void;
}

export type MenuItem = Required<MenuProps>['items'][number];

const menuItems: MenuItem[] = [
  {
    key: '/dashboard',
    icon: <DashboardOutlined />,
    // В новом кабинете пункт называется «Главная» (см. menuLabel).
    label: 'Дашборд',
  },
  // «Сегодня» — Operations Control Tower. Отдельный пункт рядом с
  // дашбордом, а не вкладка внутри хаускипинга: экран собирает
  // ситуации из задач, инспекций и межслужебных заявок сразу.
  // Виден только при включённом пилоте (/ops-today.json, гейт ниже
  // в filter) — старый дашборд не трогаем, пока польза не
  // подтверждена на фокус-группе.
  {
    key: '/ops/today',
    icon: <AlertOutlined />,
    label: 'Сегодня',
  },
  // Аналитика: PMS KPI-дашборд (выручка, ADR, RevPAR, загрузка) —
  // отдельный модуль, включается супер-админом (analytics_enabled).
  // Раньше жил внутри «Консьерж → Дашборд», что путало: это аналитика
  // по PMS-данным, а не по консьержу.
  {
    key: '/analytics',
    icon: <DashboardOutlined />,
    label: 'Аналитика',
  },
  // Шахматка: календарь броней по номерам. Виден только при
  // platform.schedule_enabled === true (гейт ниже в filter). Включается
  // супер-админом точечно (#504). По UX-запросу клиента — сразу после
  // «Дашборд», т.к. это ежедневный рабочий инструмент ресепшен.
  {
    key: '/schedule',
    icon: <CalendarOutlined />,
    label: 'Шахматка',
  },
  // Цены и квоты: сетка наличия, квот каналов и цен по датам. Модуль
  // Channel Manager (platform.channel_manager_enabled, гейт ниже).
  {
    key: '/rates-quotas',
    icon: <TableOutlined />,
    label: 'Цены и квоты',
  },
  // Бронирования — табличный список тех же броней что в Шахматке
  // (#529). Раньше пункт жил внутри «Регистрации», но это критичный
  // ежедневный инструмент ресепшен — выносим в top-level сайдбара
  // сразу после Шахматки.
  {
    key: '/registrations/bookings',
    icon: <InboxOutlined />,
    label: 'Бронирования',
  },
  // Цифровой двойник: живая карта отеля. Виден только при
  // platform.twin_enabled === true (гейт ниже в filter).
  {
    key: '/twin',
    icon: <EnvironmentOutlined />,
    label: 'Карта отеля',
  },
  {
    key: 'concierge',
    icon: <ShoppingCartOutlined />,
    label: 'Консьерж',
    children: [
      // PMS KPI-дашборд переехал в отдельный модуль «Аналитика»
      // (top-level, гейт analytics_enabled) — из Консьержа убран.
      // Обзор — первым: с него раздел открывается (заказы сейчас и за период).
      { key: '/concierge/overview', label: 'Обзор' },
      { key: '/concierge/app', label: 'Приложение' },
      { key: '/concierge/orders', label: 'Заказы' },
      { key: '/concierge/payments', label: 'Платежи' },
      { key: '/concierge/guests', label: 'Гости' },
      // Лояльность — отдельный раздел только в оболочке R3 (в старом
      // дашборде она по-прежнему внутри «Аналитики»). Гейт ниже.
      { key: '/concierge/loyalty', label: 'Лояльность' },
      { key: '/concierge/schedule', label: 'Онлайн записи' },
      { key: '/concierge/transfers', label: 'Трансферы' },
      { key: '/concierge/fitness', label: 'Фитнес-центр' },
      { key: '/concierge/events', label: 'Мероприятия' },
      { key: '/concierge/gift-cards', label: 'Подарочные сертификаты' },
      // «Карта» и «Транспорт» перенесены внутрь страницы «Приложение»
      // как табы (это контент-настройка, не ежедневная работа).
      // Старые ссылки /concierge/map и /concierge/transport
      // редиректят на /concierge/app?tab=map|transport.
    ],
  },
  {
    key: '/crm',
    icon: <MessageOutlined />,
    label: 'CRM',
  },
  {
    key: 'reviews-group',
    icon: <StarOutlined />,
    label: 'Отзывы',
    children: [
      { key: '/reviews', label: 'Отзывы' },
      { key: '/reviews/group', label: 'Свод по объектам' },
      { key: '/surveys', label: 'Опросы' },
    ],
  },
  // «Задачи» — сквозной top-level раздел (Консьерж + Хаускипинг):
  // межотдельные заявки/поручения в привычной Treema-структуре +
  // вкладка «Уборки». Заменяет пункты «Задачи» и «Заявки» внутри
  // Хаускипинга.
  {
    key: '/tasks',
    icon: <CheckSquareOutlined />,
    label: 'Задачи',
  },
  {
    key: 'housekeeping',
    icon: <HomeOutlined />,
    label: 'Хаускипинг',
    children: [
      { key: '/housekeeping/dashboard', label: 'Дашборд' },
      { key: '/housekeeping/rooms', label: 'Помещения' },
      // «Типы уборки» убраны из меню: чек-листы, время и химия
      // настраиваются в карточке категории (Помещения → Категории →
      // Уборка) — одно место вместо двух.
      { key: '/housekeeping/planner', label: 'Планировщик' },
      { key: '/housekeeping/inventory', label: 'Склад' },
      { key: '/housekeeping/reports', label: 'Отчёты' },
      { key: '/laundry', label: 'Прачечная' },
    ],
  },
  // Conference / MICE (конференц-залы). Залы/Форматы/Оборудование
  // объединены в раздел «Залы» с горизонтальными вкладками внутри.
  // Виден только если сервис conference включён супер-админом
  // (см. MENU_SERVICE_MAP ниже).
  {
    key: 'conference',
    icon: <ApartmentOutlined />,
    label: 'Конференц-залы',
    children: [
      { key: '/conference/calendar', label: 'Занятость' },
      { key: '/conference/events', label: 'Мероприятия' },
      { key: '/conference/rooms', label: 'Залы' },
      { key: '/conference/inquiries', label: 'Заявки' },
    ],
  },
  {
    key: 'registrations',
    icon: <TeamOutlined />,
    label: 'Регистрации',
    children: [
      { key: '/registrations/history', label: 'История регистраций' },
      { key: '/registrations/welcome', label: 'Welcome письма' },
      // «Бронирования» вынесены в top-level сайдбара (#529).
      { key: '/registrations/documents', label: 'Документы' },
      { key: '/registrations/ekeys', label: 'Электронные ключи' },
    ],
  },
  {
    key: 'wifi',
    icon: <WifiOutlined />,
    label: 'Wi-Fi',
    children: [
      // Три раздела вместо семи: ручную регистрацию гостя на практике
      // не используют, а с неё начинался весь Wi-Fi. Активные
      // подключения и история — ссылками со «Статистики», конструктор
      // шаблонов — ссылкой с «Лендингов». Адреса страниц сохранены.
      { key: '/wifi/statistics', label: 'Статистика' },
      { key: '/wifi/landing-v2', label: 'Лендинги' },
      // Старый конструктор — только тем, у кого есть его лендинги
      // (гейт ниже). Новым отелям — только «Лендинги» (v2).
      { key: '/wifi/constructor', label: 'Конструктор (старый)' },
      { key: '/wifi/trusted', label: 'Доверенные устройства' },
    ],
  },
  // CMS: редактирование публичного сайта-визитки отеля.
  // Виден только при platform.cms_enabled === true (гейт ниже в filter).
  {
    key: '/site',
    icon: <GlobalOutlined />,
    label: 'Сайт',
  },
  {
    key: 'settings',
    icon: <SettingOutlined />,
    label: 'Настройки',
    children: [
      // «Организация» скрыта из меню — все данные объединены в «Объекты».
      { key: '/settings/platforms', label: 'Объекты' },
      // Номерной фонд: категории и номера. По UX-запросу отеля без
      // PMS-интеграции точка входа должна быть в Настройках — это
      // конфигурация объекта, а не ежедневная работа горничной.
      // /settings/rooms — алиас на RoomsPage (тот же компонент, что
      // и /housekeeping/rooms). См. RouterProvider.tsx.
      { key: '/settings/rooms', label: 'Номерной фонд' },
      // Тарифы и прайсы для онлайн-бронирования (#518). Управляет
      // условиями продажи (питание, отмена) и ценами по категориям
      // на диапазон дат. Используется Шахматкой при создании броней.
      { key: '/settings/tariffs', label: 'Тарифы' },
      // Channel Manager: выгрузка цен/наличия в OTA и приём броней в
      // шахматку (Booking.com, Яндекс Путешествия, Островок).
      { key: '/settings/channel-manager', label: 'Channel Manager' },
      { key: '/settings/tv-devices', label: 'ТВ-устройства' },
      { key: '/settings/reports', label: 'Документы' },
    ],
  },
];

/**
 * Маппинг ключей меню → ключи сервисов платформы.
 * Если ключ присутствует — пункт показывается только если сервис enabled.
 * Если ключа нет — пункт показывается всегда.
 */
const MENU_SERVICE_MAP: Record<string, string> = {
  concierge: 'concierge',
  '/crm': 'concierge',
  'reviews-group': 'rep',
  housekeeping: 'housekeeping',
  conference: 'conference',
  registrations: 'online_checkin',
  wifi: 'wifi',
};

/**
 * Маппинг ключей меню → ключи разделов для per-user настройки
 * (Account.web_sections_override). Синхронизирован с бекендом
 * (serverapp/models/accounting/web_sections.py) и карточкой
 * пользователя (Настройки → Пользователи → «Разделы сайта»).
 */
const MENU_SECTION_KEYS: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/twin': 'twin',
  concierge: 'concierge',
  '/crm': 'crm',
  '/requests': 'requests',
  'reviews-group': 'reviews',
  housekeeping: 'housekeeping',
  registrations: 'registrations',
  '/schedule': 'schedule',
  wifi: 'wifi',
  '/site': 'site',
  settings: 'settings',
};

/**
 * Модель меню кабинета: пункты с бейджами и всеми гейтами (сервисы
 * объекта, флаги модулей, разделы учётки, роли). Одна на сайдбар и на
 * выпадающее меню шапки R3 — фильтры не расходятся.
 */
/**
 * Поллинг бейджей (заказы, фитнес, прачечная) и название фитнеса по
 * активной платформе. Живёт в постоянной части шапки — в Sidebar или
 * в шапке R3, — а не в useMenuModel: меню R3 монтируется только при
 * открытии, и при закрытии останавливало бы счётчики.
 */
export function useSidebarCounters() {
  const { platforms } = useRootStore();
  const pid = platforms.activePlatformId;
  useEffect(() => {
    if (pid) {
      sidebarCountersStore.start(pid);
      if (!changelogStore.loaded) void changelogStore.fetch();
    } else {
      sidebarCountersStore.stop();
    }
    return () => sidebarCountersStore.stop();
  }, [pid]);
}

export function useMenuModel(onNavigate?: () => void) {
  const location = useLocation();
  const navigate = useNavigate();
  const store = useRootStore();
  const { profile, platforms } = store;
  const platformLogo = platforms.activePlatform?.logo;

  // Периодически обновляем счётчик непрочитанных сообщений
  useEffect(() => {
    globalChatStore.fetchUnreadCount();
    const interval = setInterval(() => globalChatStore.fetchUnreadCount(), 30_000);
    return () => clearInterval(interval);
  }, []);

  // Карта включённых сервисов
  const servicesMap: Record<string, boolean> = {};
  const platform = platforms.activePlatform;
  if (platform?.services) {
    for (const svc of platform.services) {
      servicesMap[svc.name] = svc.enabled;
    }
  }

  const unreadCount = globalChatStore.unreadCount;
  const ordersNew = sidebarCountersStore.ordersNew;
  const laundryPending = sidebarCountersStore.laundryPending;
  const fitnessPending = sidebarCountersStore.fitnessPending;
  const interdeptInbox = sidebarCountersStore.interdeptInbox;
  // Пилот «Сегодня»: файл перечитывается сам, пункт меню появляется
  // и исчезает без перезагрузки страницы.
  const opsTodayEnabled = useOpsTodayFlag();
  const conferenceInquiriesNew = sidebarCountersStore.conferenceInquiriesNew;
  // Имя пункта меню «Фитнес-центр» — берётся из FitnessConfig.display_name
  // отеля (например, «Аквацентр» у Дружбы, «Фитнес-центр» у Гринвуда).
  // Если конфига нет — дефолт «Wellness» в сторе.
  const fitnessLabel = sidebarCountersStore.fitnessLabel;

  // Универсальный label с бейджем — используется и для верхнего уровня
  // (CRM), и для children (Прачечная, Фитнес-центр).
  const withBadge = (text: string, count: number) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {text}
      {count > 0 && (
        <Badge count={count} size="small" style={{ flexShrink: 0 }} />
      )}
    </span>
  );

  // Динамический список меню. CRM, Прачечная, Фитнес-центр получают
  // badge'и с количеством необработанных.
  const decorateChildren = (children: MenuItem[] | undefined): MenuItem[] =>
    (children || []).map((child) => {
      if (!child || !('key' in child) || child.key == null) return child;
      if (child.key === '/concierge/orders') {
        return { ...child, label: withBadge('Заказы', ordersNew) };
      }
      if (child.key === '/concierge/fitness') {
        return { ...child, label: withBadge(fitnessLabel, fitnessPending) };
      }
      if (child.key === '/laundry') {
        return { ...child, label: withBadge('Прачечная', laundryPending) };
      }
      if (child.key === '/conference/inquiries') {
        return { ...child, label: withBadge('Заявки', conferenceInquiriesNew) };
      }
      if (child.key === '/requests') {
        return { ...child, label: withBadge('Заявки', interdeptInbox) };
      }
      return child;
    });

  const dynamicMenuItems: MenuItem[] = menuItems.map((item) => {
    if (!item || !('key' in item)) return item;
    if (item.key === '/crm') {
      return { ...item, label: withBadge('CRM', unreadCount) };
    }
    if (item.key === '/requests') {
      return { ...item, label: withBadge('Заявки', interdeptInbox) };
    }
    if (item.key === '/tasks') {
      return { ...item, label: withBadge('Задачи', interdeptInbox) };
    }
    // Группы с детьми (Консьерж, Хаускипинг) — добавим в groups+ badge на
    // самом группа-узле как сумму, чтобы было видно даже когда подменю
    // свёрнуто.
    if ('children' in item && item.children) {
      const decorated = decorateChildren(item.children as MenuItem[]);
      let groupBadge = 0;
      // Сумма по разделу: когда подменю свёрнуто, иначе новые заказы
      // не видны вовсе — а именно их и пропускают.
      if (item.key === 'concierge') groupBadge = ordersNew + fitnessPending;
      else if (item.key === 'housekeeping') groupBadge = laundryPending;
      else if (item.key === 'conference') groupBadge = conferenceInquiriesNew;
      const baseLabel =
        typeof item.label === 'string' ? item.label : String(item.label ?? '');
      return {
        ...item,
        label: groupBadge > 0 ? withBadge(baseLabel, groupBadge) : item.label,
        children: decorated,
      };
    }
    return item;
  });

  // Гейты, которые не ложатся в MENU_SERVICE_MAP (сервисы платформы).
  // «Сайт» — модуль CMS, включается супер-админом через Platform.cms_enabled.
  // Сервис не заводим, чтобы не ломать сериализатор Platform.services.
  const cmsEnabled = (platform as any)?.cms_enabled === true;
  // Старый «Конструктор» (lang-gen, шаблоны Connect One) прячем, когда к
  // Wi-Fi площадки привязан Landing v2: гость его уже не видит, а два
  // конструктора рядом только путают отель. Флаг отдаёт PlatformSerializer.
  const landingV2Bound = (platform as any)?.wifi_landing_v2_bound === true;
  const legacyLandings = (platform as any)?.wifi_legacy_landings === true;

  // Per-user видимость разделов: Account.web_sections_override.
  // null/undefined — стандартное меню по роли (как раньше); массив —
  // жёсткий набор: показываем ровно эти разделы (поверх фильтра по
  // сервисам платформы). Настраивается в карточке сотрудника
  // (Настройки → Пользователи → «Разделы сайта»). Роль/SLVN продолжает
  // отвечать за права API — здесь только меню.
  const rawSectionsOverride = (
    profile as { web_sections_override?: string[] | null } | null
  )?.web_sections_override;
  const sectionsOverride = Array.isArray(rawSectionsOverride)
    ? new Set(rawSectionsOverride)
    : null;

  // Фильтруем меню по включённым сервисам + per-user override
  const filteredMenuItems = dynamicMenuItems.filter((item) => {
    if (!item || !('key' in item) || item.key == null) return true;
    // Per-user override: раздел должен быть отмечен в карточке сотрудника.
    if (sectionsOverride !== null) {
      const sectionKey = MENU_SECTION_KEYS[item.key as string];
      if (sectionKey && !sectionsOverride.has(sectionKey)) return false;
    }
    // Отдельный гейт для CMS — не проходит через MENU_SERVICE_MAP.
    if (item.key === '/site') return cmsEnabled;
    // «Сегодня» — пилот Control Tower, включается файлом
    // /ops-today.json поимённо на площадки и учётки.
    // «Дашборд» в новом кабинете — главная R3 (обзор отеля и дня,
    // решение 03.11.2026); очереди и действия — в «Сегодня».
    if (item.key === '/ops/today') {
      return opsTodayEnabled;
    }
    // Цифровой двойник — по флагу платформы (включает супер-админ).
    if (item.key === '/twin') {
      return (platform as any)?.twin_enabled === true;
    }
    // Шахматка — отдельный модуль, включается супер-админом (#504).
    if (item.key === '/schedule') {
      return (platform as any)?.schedule_enabled === true;
    }
    // Цены и квоты — часть модуля Channel Manager.
    if (item.key === '/rates-quotas') {
      return (platform as any)?.channel_manager_enabled === true;
    }
    // Аналитика (PMS KPI-дашборд) — модуль, включается супер-админом.
    if (item.key === '/analytics') {
      return (platform as any)?.analytics_enabled === true;
    }
    // «Бронирования» — только если включён Онлайн чек-ин или Шахматка
    // (иначе разделу нечего показывать: это список тех же броней).
    if (item.key === '/registrations/bookings') {
      return (
        servicesMap['online_checkin'] === true
        || (platform as any)?.schedule_enabled === true
      );
    }
    const svcKey = MENU_SERVICE_MAP[item.key as string];
    if (!svcKey) return true; // нет привязки к сервису — показываем
    return servicesMap[svcKey] !== false; // показываем если enabled или отсутствует (нет данных)
  }).map((item) => {
    // Гейты на уровне children: фильтр выше работает только по верхнему
    // уровню. Channel Manager в «Настройках» — отдельный модуль.
    if (item && 'key' in item && item.key === 'settings' && 'children' in item && item.children) {
      const cmEnabled = (platform as any)?.channel_manager_enabled === true;
      const kids = (item.children as MenuItem[]).filter(
        (child) => cmEnabled || !(child && 'key' in child && child.key === '/settings/channel-manager'),
      );
      // В R3 первым пунктом — хаб «Все настройки» (плитки по смыслу).
      return {
        ...item,
        children: shellChrome.r3 ? [{ key: '/settings', label: 'Все настройки' } as MenuItem, ...kids] : kids,
      };
    }
    // В новом кабинете «Дашборд» — «Главная» (обзор отеля и дня).
    if (item && 'key' in item && item.key === '/dashboard' && shellChrome.r3) {
      return { ...item, label: 'Главная' } as MenuItem;
    }
    // «Лояльность» — раздел оболочки R3; без неё пункт не нужен.
    if (item && 'key' in item && item.key === 'concierge' && 'children' in item && item.children && !shellChrome.r3) {
      return {
        ...item,
        children: (item.children as MenuItem[]).filter(
          (child) => !(child && 'key' in child && child.key === '/concierge/loyalty'),
        ),
      };
    }
    // В R3 «Уборки» живут в «Хаускипинге», а не вкладкой «Задач»
    // (решение 30.10.2026). Без R3 меню прежнее.
    if (item && 'key' in item && item.key === 'housekeeping' && 'children' in item && item.children && shellChrome.r3) {
      const kids = item.children as MenuItem[];
      const at = kids.findIndex((c) => c && 'key' in c && c.key === '/housekeeping/dashboard') + 1;
      return {
        ...item,
        children: [...kids.slice(0, at), { key: '/housekeeping/tasks', label: 'Уборки' } as MenuItem, ...kids.slice(at)],
      };
    }
    // Старый «Конструктор» внутри WiFi — скрыть при Landing v2.
    if (item && 'key' in item && item.key === 'wifi' && 'children' in item && item.children) {
      return {
        ...item,
        children: (item.children as MenuItem[]).filter(
          (child) =>
            !(child && 'key' in child && child.key === '/wifi/constructor' && (landingV2Bound || !legacyLandings)),
        ),
      };
    }
    return item;
  });

  // Роли:
  // - is_superadmin — видит весь «Система» + «Маркетинг», может войти в любую
  //   платформу и получить полный доступ.
  // - is_support — новая роль техподдержки (#189): видит только «Поддержка»
  //   и переключатель платформ; после входа в платформу получает полный
  //   доступ, как обычный админ объекта (чтобы разбирать баги клиента).
  // - обычный пользователь — видит основное меню по своим правам.
  const isSuperAdmin = profile?.is_superadmin === true;
  const isSupport = profile?.is_support === true;
  // Дилер — партнёр, управляющий своими отелями: кабинет дилера
  // («Мои отели», «Взаиморасчёты») + вход в любой свой отель.
  const isDealer = !isSuperAdmin && !isSupport && profile?.dealer != null;
  const isStaff = isSuperAdmin || isSupport || isDealer;
  const isEnterMode = isStaff && platforms.superadminEnterMode;

  // Для техподдержки оставляем только раздел с тикетами. Системные
  // инструменты (Платформы/Лиды/Пользователи/Новости) — только супер-админу.
  const supportOnlyMenuItems: MenuItem[] = [
    {
      key: 'system',
      icon: <CloudServerOutlined />,
      label: 'Система',
      children: [{ key: '/system/support', label: 'Поддержка' }],
    },
  ];

  const dealerMenuItems: MenuItem[] = [
    {
      key: 'dealer',
      icon: <CloudServerOutlined />,
      label: profile?.dealer_name || 'Дилер',
      children: [
        { key: '/dealer/platforms', label: 'Мои отели' },
        { key: '/dealer/support', label: 'Поддержка отелей' },
        { key: '/dealer/invoices', label: 'Взаиморасчёты' },
        { key: '/dealer/info', label: 'Информация' },
      ],
    },
  ];

  const systemMenuItems: MenuItem[] = [
    {
      key: 'system',
      icon: <CloudServerOutlined />,
      label: 'Система',
      children: [
        { key: '/system/health', label: 'Здоровье системы' },
        { key: '/system/flight-watch', label: 'Flight Watch · B2C' },
        { key: '/system/platforms', label: 'Все объекты' },
        { key: '/system/billing', label: 'Расчёты' },
        { key: '/system/dealers', label: 'Дилеры' },
        { key: '/system/pending', label: 'Ожидают активации' },
        { key: '/system/leads', label: 'Лиды' },
        { key: '/system/onlinevisa/agents', label: 'OnlineVisa · Агенты' },
        { key: '/system/onlinevisa/partner-applications', label: 'OnlineVisa · Заявки партнёров' },
        { key: '/system/news', label: 'Новости' },
        { key: '/system/users', label: 'Все пользователи' },
        { key: '/system/wifi', label: 'WiFi мониторинг' },
        { key: '/system/wifi-setup', label: 'WiFi настройка' },
        { key: '/system/support', label: 'Поддержка' },
        { key: '/system/partners', label: 'Партнёры' },
      ],
    },
    {
      key: 'marketing',
      icon: <RocketOutlined />,
      label: 'Маркетинг',
      children: [
        { key: '/system/marketing/hotels', label: 'База отелей' },
        { key: '/system/marketing/kanban', label: 'Воронка' },
        { key: '/system/marketing/analytics', label: 'Аналитика' },
        { key: '/system/marketing/campaigns', label: 'Рассылки' },
        { key: '/system/marketing/send', label: 'Отправка кампании' },
        { key: '/system/marketing/lists', label: 'Списки Unisender' },
        { key: '/system/marketing/templates', label: 'Шаблоны писем' },
        { key: '/system/marketing/campaign-stats', label: 'Статистика кампании' },
        { key: '/system/marketing/tracking', label: 'Трекинг по отелям' },
      ],
    },
  ];

  // Сотрудник фитнес-стенда: видит только один пункт меню. Решение
  // вынесено выше других ролей, чтобы super-admin / support, попавшие
  // в enter-mode на платформу с fitness_staff-аккаунтом, оставались
  // на стороне «как admin», а не получали этот ограниченный набор.
  const groupId =
    (profile as { group?: string; group_id?: string } | null)?.group ??
    (profile as { group_id?: string } | null)?.group_id ??
    null;
  const isFitnessStaff =
    !isSuperAdmin && !isSupport && groupId === 'fitness_staff';
  // #378: аниматор — единственный пункт меню «Расписание» (онлайн-записи).
  const isAnimator =
    !isSuperAdmin && !isSupport && groupId === 'animator';
  const isLaundryStaff =
    !isSuperAdmin && !isSupport && groupId === 'laundry_staff';

  let allMenuItems: MenuItem[];
  if (isFitnessStaff) {
    // Только пункт wellness-модуля. Имя берём из FitnessConfig.display_name
    // (для аквацентра Дружбы — «Аквацентр», для Гринвуда —
    // «Фитнес-центр»; дефолт «Wellness»).
    allMenuItems = [
      {
        key: '/concierge/fitness',
        icon: <ShoppingCartOutlined />,
        label: fitnessLabel,
      },
    ];
  } else if (isLaundryStaff) {
    // Сотрудник прачечной — только раздел «Прачечная».
    allMenuItems = [
      {
        key: '/laundry',
        icon: <HomeOutlined />,
        label: withBadge('Прачечная', laundryPending),
      },
    ];
  } else if (isAnimator) {
    // Аниматор видит только страницу расписания онлайн-записей.
    // Внутри неё уже есть табы «Запись на услуги», «Записи», «События».
    allMenuItems = [
      {
        key: '/concierge/schedule',
        icon: <ShoppingCartOutlined />,
        label: 'Расписание',
      },
    ];
  } else if (isStaff && !isEnterMode) {
    // Staff в «системном» режиме: видит либо полное системное меню
    // (супер-админ), либо кабинет дилера, либо укороченное (техподдержка).
    allMenuItems = isSuperAdmin
      ? systemMenuItems
      : isDealer
      ? dealerMenuItems
      : supportOnlyMenuItems;
  } else {
    // Обычный пользователь ИЛИ staff в enter-mode: меню платформы.
    allMenuItems = [
      ...filteredMenuItems,
      {
        key: '/support',
        icon: <MessageOutlined />,
        label: 'Поддержка',
      },
    ];
  }

  const handleClick: MenuProps['onClick'] = ({ key }) => {
    if (key.startsWith('/')) {
      navigate(key);
      onNavigate?.();
    }
  };

  const handleExitPlatform = () => {
    platforms.exitPlatform();
    navigate(isDealer ? '/dealer/platforms' : '/system/platforms');
    onNavigate?.();
  };

  return {
    location, navigate, platforms, platform, platformLogo, servicesMap,
    isEnterMode, isDealer, isSuperAdmin, isStaff, allMenuItems, handleClick, handleExitPlatform,
  };
}

export const Sidebar = observer(function Sidebar({ collapsed, onNavigate }: SidebarProps) {
  useSidebarCounters();
  const {
    location, navigate, platforms, platformLogo,
    isEnterMode, isDealer, isSuperAdmin, allMenuItems, handleClick, handleExitPlatform,
  } = useMenuModel(onNavigate);
  return (
    <div className="flex h-full flex-col">
      {/* Баннер: суперадмин просматривает объект */}
      {isEnterMode && (
        <div
          style={{
            background: '#fffbe6',
            borderBottom: '1px solid #ffe58f',
            padding: collapsed ? '8px 4px' : '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <Button
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={handleExitPlatform}
            style={{ flexShrink: 0 }}
          >
            {!collapsed && (isDealer ? 'Мои отели' : 'В систему')}
          </Button>
          {!collapsed && (
            <span style={{ fontSize: 11, color: '#ad6800', lineHeight: 1.3, overflow: 'hidden' }}>
              {platforms.activePlatform?.name || `Объект #${platforms.activePlatformId}`}
            </span>
          )}
        </div>
      )}

      {/* Логотип: лого отеля (если загружено) или HKeeper */}
      <div
        style={{
          minHeight: 'var(--header-height)',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          padding: collapsed ? '12px 8px' : '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 10,
        }}
      >
        {platformLogo ? (
          // Логотип отеля может быть тёмным на прозрачном фоне —
          // подкладываем белую плашку, чтобы читался на тёмном сайдбаре.
          <span
            style={{
              background: '#ffffff',
              borderRadius: 6,
              padding: '4px 8px',
              display: 'inline-flex',
              alignItems: 'center',
              flexShrink: 0,
              maxWidth: collapsed ? 48 : 168,
            }}
          >
            <img
              src={platformLogo}
              alt={platforms.activePlatform?.name || 'Hotel'}
              style={{
                height: collapsed ? 28 : 32,
                maxWidth: collapsed ? 36 : 152,
                objectFit: 'contain',
              }}
            />
          </span>
        ) : (
          <>
            <img
              src={brand.logo}
              alt={brand.name}
              style={{
                height: 28,
                width: 'auto',
                // Белая версия логотипа для тёмного сайдбара
                filter: brand.id === 'hkeeper' ? 'brightness(0) invert(1)' : undefined,
                flexShrink: 0,
              }}
            />
            {!collapsed && (
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: '#ffffff',
                  whiteSpace: 'nowrap',
                }}
              >
                {brand.name}
              </span>
            )}
          </>
        )}
      </div>

      {/* Меню */}
      <Menu
        mode="inline"
        theme="dark"
        selectedKeys={[location.pathname]}
        defaultOpenKeys={isEnterMode ? [] : isSuperAdmin ? ['system'] : []}
        // Кабинет дилера: пунктов мало — держим группу всегда раскрытой
        // (контролируемый openKeys без возможности свернуть).
        {...(isDealer && !isEnterMode
          ? { openKeys: ['dealer'], expandIcon: () => null }
          : {})}
        items={allMenuItems}
        onClick={handleClick}
        style={{
          background: 'transparent',
          borderRight: 'none',
          flex: 1,
          paddingTop: 8,
        }}
      />

      {/* Блок техподдержки */}
      {!collapsed && (
        <div
          style={{
            padding: '12px 16px',
            borderTop: '1px solid rgba(255,255,255,0.12)',
            fontSize: 12,
            lineHeight: '20px',
            color: 'var(--color-sidebar-text)',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
            <CustomerServiceOutlined style={{ marginRight: 6 }} />
            Техподдержка
          </div>
          <div style={{ opacity: 0.8 }}>
            <PhoneOutlined style={{ marginRight: 6 }} />
            <a href="tel:+74954097578" style={{ color: 'inherit' }}>+7 (495) 409-75-78</a>
          </div>
          <div style={{ opacity: 0.8, marginBottom: 8 }}>
            <MailOutlined style={{ marginRight: 6 }} />
            <a href={`mailto:${brand.email}`} style={{ color: 'inherit' }}>{brand.email}</a>
          </div>
          <Button
            type="primary"
            size="small"
            block
            onClick={() => {
              const path = isSuperAdmin ? '/system/support' : '/support';
              navigate(path);
              onNavigate?.();
            }}
          >
            Оставить заявку
          </Button>
          <div style={{ marginTop: 8, opacity: 0.4, textAlign: 'center' }}>
            © {new Date().getFullYear()} {brand.name}
          </div>
          <div style={{ marginTop: 4, opacity: 0.3, textAlign: 'center', fontSize: 10 }}>
            {typeof __APP_COMMIT__ !== 'undefined' ? __APP_COMMIT__ : 'dev'}
          </div>
        </div>
      )}
    </div>
  );
});
